import type { SpreadDefinition } from './core';
import { createSpreadDefinition } from '../internal/builders';

export const TREE_OF_LIFE_SPREAD_DEFINITION: SpreadDefinition = createSpreadDefinition({
  id: 'tree-of-life',
  name: 'Albero della Vita',
  slug: 'tree-of-life',
  category: 'kabbalistic',
  description: 'Dieci posizioni sulle Sephiroth, con geometria coerente e lettura a nodi dell’albero.',
  ritual_note: 'Richiede attenzione simbolica e disponibilità a leggere gli assi sephirotici, non solo singole carte.',
  opening_invocation: 'Apro l’Albero, dispongo i nodi e ascolto la corrente fra le Sephiroth.',
  closing_words: 'Richiudo l’Albero e lascio decantare il ponte fra i suoi mondi.',
  difficulty: 'initiatic',
  is_lilith: false,
  tree_of_life_mapping: true,
  family: 'tree',
  intent: ['kabbalistic', 'spiritual'],
  semantic_tags: ['initiatic', 'ritual', 'self-study'],
  layout_id: 'tree-of-life-10',
  position_specs: [
    { role_id: 'significator', name: 'Kether', description: 'Corona, origine e scintilla dell’intero quadro.', sephirah_correspondence: 'Kether' },
    { role_id: 'resource', name: 'Chokmah', description: 'Impulso sapienziale, corrente attiva e seminale.', sephirah_correspondence: 'Chokmah' },
    { role_id: 'truth', name: 'Binah', description: 'Forma, limite e comprensione strutturante.', sephirah_correspondence: 'Binah' },
    { role_id: 'resource', name: 'Chesed', description: 'Espansione, grazia e sostegno del campo.', sephirah_correspondence: 'Chesed' },
    { role_id: 'challenge', name: 'Geburah', description: 'Disciplina, taglio e potere discriminante.', sephirah_correspondence: 'Geburah' },
    { role_id: 'message', name: 'Tiphareth', description: 'Cuore, armonia e centro irradiativo della lettura.', sephirah_correspondence: 'Tiphareth' },
    { role_id: 'desire', name: 'Netzach', description: 'Desiderio, impulso vitale e persistenza del movimento.', sephirah_correspondence: 'Netzach' },
    { role_id: 'action', name: 'Hod', description: 'Lingua, strategia, simbolizzazione e forma mentale.', sephirah_correspondence: 'Hod' },
    { role_id: 'integration', name: 'Yesod', description: 'Fondazione immaginale e condensazione del processo.', sephirah_correspondence: 'Yesod' },
    { role_id: 'outcome', name: 'Malkuth', description: 'Manifestazione, corpo e mondo materiale del risultato.', sephirah_correspondence: 'Malkuth' },
  ],
  keywords: ['kabbalah', 'albero', 'sephiroth', 'geometria sacra'],
  recommended_for_questions: [
    'Voglio leggere la situazione attraverso i nodi dell’Albero della Vita.',
    'Mi serve una struttura simbolica completa e non solo narrativa.',
  ],
  contraindications: ['Non consigliata a livello beginner o per domande estremamente rapide.'],
  focus_mode_ids: [],
  ritual_intensity: 5,
});

export const SENTIERO_DOMINANTE_SPREAD_DEFINITION: SpreadDefinition = createSpreadDefinition({
  id: 'sentiero-dominante',
  name: 'Sentiero Dominante',
  slug: 'sentiero-dominante',
  category: 'kabbalistic',
  description: 'Tre carte per cogliere il sentiero prevalente, il ponte critico e la qualità dell’attraversamento.',
  ritual_note: null,
  opening_invocation: null,
  closing_words: null,
  difficulty: 'advanced',
  is_lilith: false,
  tree_of_life_mapping: false,
  family: 'triad',
  intent: ['kabbalistic', 'clarity'],
  semantic_tags: ['initiatic', 'self-study'],
  layout_id: 'linear-3',
  position_specs: [
    { role_id: 'message', name: 'Sentiero in apertura', description: 'Quale sentiero simbolico si sta aprendo?' },
    { role_id: 'challenge', name: 'Attrito del passaggio', description: 'Quale resistenza o prova ne definisce il costo?' },
    { role_id: 'integration', name: 'Attraversamento', description: 'Come può essere attraversato il sentiero con coerenza?' },
  ],
  keywords: ['sentiero', 'ponte', 'passaggio', 'kabbalistico'],
  recommended_for_questions: [
    'Quale sentiero è dominante nella mia fase attuale?',
    'Dove si concentra la prova del passaggio?',
  ],
  contraindications: ['Non ideale per un quadro completo dell’anno o di una relazione.'],
  focus_mode_ids: [],
  ritual_intensity: 3,
});

export const PONTE_SEPHIROTICO_SPREAD_DEFINITION: SpreadDefinition = createSpreadDefinition({
  id: 'ponte-sephirotico',
  name: 'Ponte Sephirotico',
  slug: 'ponte-sephirotico',
  category: 'kabbalistic',
  description: 'Cinque carte per leggere un ponte fra nodi sephirotici: sostegno, attrito, centro, gesto e sbocco.',
  ritual_note: null,
  opening_invocation: null,
  closing_words: null,
  difficulty: 'advanced',
  is_lilith: false,
  tree_of_life_mapping: false,
  family: 'line',
  intent: ['kabbalistic', 'healing', 'spiritual'],
  semantic_tags: ['initiatic', 'healing', 'ritual'],
  layout_id: 'line-5',
  position_specs: [
    { role_id: 'resource', name: 'Polo di origine', description: 'Quale Sephirah o qualità sostiene la partenza?', sephirah_correspondence: 'Chesed' },
    { role_id: 'challenge', name: 'Attrito del ponte', description: 'Quale polo chiede disciplina o sacrificio?', sephirah_correspondence: 'Geburah' },
    { role_id: 'truth', name: 'Centro del ponte', description: 'Qual è il cuore armonico da non perdere?', sephirah_correspondence: 'Tiphareth' },
    { role_id: 'ritual_action', name: 'Passaggio operativo', description: 'Quale gesto rende praticabile il ponte?', sephirah_correspondence: 'Yesod' },
    { role_id: 'integration', name: 'Manifestazione', description: 'Dove si posa il ponte nel reale?', sephirah_correspondence: 'Malkuth' },
  ],
  keywords: ['ponte', 'sephirotico', 'centro', 'integrazione'],
  recommended_for_questions: [
    'Come unisco due poli simbolici senza collassare in uno solo?',
    'Quale ponte interiore o spirituale sto cercando di costruire?',
  ],
  contraindications: ['Non ideale come prima introduzione assoluta al linguaggio sephirotico.'],
  focus_mode_ids: [],
  ritual_intensity: 4,
});

export const KABBALISTIC_SPREAD_DEFINITIONS: SpreadDefinition[] = [
  TREE_OF_LIFE_SPREAD_DEFINITION,
  SENTIERO_DOMINANTE_SPREAD_DEFINITION,
  PONTE_SEPHIROTICO_SPREAD_DEFINITION,
];
